/**
 * Telegram HTML (decision 45).
 *
 * Message text the operator formats is stored and sent as Telegram's HTML
 * parse mode, which is sturdier than MarkdownV2's escaping rules. This file
 * is the one allowlist, shared by the panel (preview) and the worker (save
 * and send): only the tags Telegram documents survive, attributes are
 * rebuilt rather than copied, and everything else becomes plain text — so
 * nothing an operator types can inject markup into the panel or break a
 * send.
 */

const ENTITY = /^&(lt|gt|amp|quot|#[0-9]{1,7}|#x[0-9a-fA-F]{1,6});/;
const LINK_SCHEME = /^(https?:\/\/|tg:\/\/)/i;
const TIME_FORMAT = /^(r|w?[dD]?[tT]?)$/;

/** Canonical name for each accepted tag. */
const TAG_ALIASES: Record<string, string> = {
  b: "b",
  strong: "b",
  i: "i",
  em: "i",
  u: "u",
  ins: "u",
  s: "s",
  strike: "s",
  del: "s",
  "tg-spoiler": "tg-spoiler",
  a: "a",
  code: "code",
  pre: "pre",
  blockquote: "blockquote",
  "tg-time": "tg-time",
};

function escapeText(text: string): string {
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === "&") {
      const entity = ENTITY.exec(text.slice(i));
      if (entity) {
        out += entity[0];
        i += entity[0].length - 1;
      } else {
        out += "&amp;";
      }
    } else if (ch === "<") out += "&lt;";
    else if (ch === ">") out += "&gt;";
    else if (ch === '"') out += "&quot;";
    else out += ch;
  }
  return out;
}

function attr(attrs: string, name: string): string | null {
  const match = new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, "i").exec(attrs);
  if (!match) return null;
  return match[2] ?? match[3] ?? match[4] ?? "";
}

function hasFlag(attrs: string, name: string): boolean {
  return new RegExp(`(^|\\s)${name}(\\s|=|$)`, "i").test(attrs);
}

const quote = (value: string) => value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");

/**
 * The opening tag to emit for an accepted element, or null to drop the
 * element's tags (keeping its text).
 */
function openTag(name: string, attrs: string, inPre: boolean): string | null {
  switch (name) {
    case "tg-spoiler":
      return "<tg-spoiler>";
    case "a": {
      const href = attr(attrs, "href")?.trim();
      return href && LINK_SCHEME.test(href) ? `<a href="${quote(href)}">` : null;
    }
    case "code": {
      const cls = attr(attrs, "class");
      const lang = cls && /^language-[A-Za-z0-9_+#.-]{1,30}$/.test(cls) ? cls : null;
      return inPre && lang ? `<code class="${lang}">` : "<code>";
    }
    case "blockquote":
      return hasFlag(attrs, "expandable") ? "<blockquote expandable>" : "<blockquote>";
    case "tg-time": {
      const unix = attr(attrs, "unix");
      if (!unix || !/^[0-9]{1,12}$/.test(unix)) return null;
      const format = attr(attrs, "format");
      return format !== null && TIME_FORMAT.test(format)
        ? `<tg-time unix="${unix}" format="${format}">`
        : `<tg-time unix="${unix}">`;
    }
    default:
      return `<${name}>`;
  }
}

export function sanitizeTgHtml(input: string): string {
  const token = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)((?:\s[^<>]*)?)\/?>/g;
  const stack: string[] = [];
  let out = "";
  let last = 0;

  const closeTo = (name: string) => {
    while (stack.length > 0) {
      const top = stack.pop()!;
      out += `</${top}>`;
      if (top === name) return;
    }
  };

  for (let m = token.exec(input); m; m = token.exec(input)) {
    out += escapeText(input.slice(last, m.index));
    last = m.index + m[0].length;
    const closing = m[1] === "/";
    const raw = m[2]!.toLowerCase();
    const attrs = m[3] ?? "";

    if (raw === "br") {
      out += "\n";
      continue;
    }
    // `<span class="tg-spoiler">` is a spoiler; its `</span>` carries no
    // class, so a closing span ends the innermost spoiler.
    const spoilerSpan =
      raw === "span" &&
      (closing ? stack.includes("tg-spoiler") : /\btg-spoiler\b/.test(attr(attrs, "class") ?? ""));
    const name = spoilerSpan ? "tg-spoiler" : TAG_ALIASES[raw];
    if (!name) {
      out += escapeText(m[0]);
      continue;
    }

    if (closing) {
      // The close of a dropped element goes with it.
      const dropped = stack.lastIndexOf(`!${name}`);
      if (dropped !== -1 && dropped > stack.lastIndexOf(name)) {
        stack.splice(dropped, 1);
        continue;
      }
      // A close for something never opened is dropped; one for an outer
      // element closes whatever is still open inside it first.
      if (stack.includes(name)) closeTo(name);
      continue;
    }

    const tag = openTag(name, attrs, stack.includes("pre"));
    if (tag === null) {
      // Dropped element: its matching close must be dropped too.
      stack.push(`!${name}`);
      continue;
    }
    out += tag;
    stack.push(name);
  }
  out += escapeText(input.slice(last));
  while (stack.length > 0) {
    const top = stack.pop()!;
    if (!top.startsWith("!")) out += `</${top}>`;
  }
  // Placeholders for dropped elements leave no trace.
  return out.replace(/<\/!\w[\w-]*>/g, "");
}

const NAMED: Record<string, string> = { lt: "<", gt: ">", amp: "&", quot: '"' };

/** What the player reads: tags removed, entities decoded. */
export function tgHtmlToPlain(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&(lt|gt|amp|quot);/g, (_, name: string) => NAMED[name] ?? "")
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#([0-9]+);/g, (_, dec: string) => String.fromCodePoint(Number(dec)));
}

/** Length as Telegram counts it against its limits: the visible text. */
export function visibleLength(html: string): number {
  return [...tgHtmlToPlain(html)].length;
}
